/**
 * specs/product-kernel/dashboard-ukrainian-localization.md
 *
 * `useT()` — the dashboard's only translation hook. No Provider needed (see store.ts for why);
 * every component calls `const { t, locale } = useT()` directly.
 */

import { en } from './en';
import { uk } from './uk';
import { useLocaleStore } from './store';
import { INTL_LOCALE, type Locale } from './types';

export type { Locale } from './types';
export { INTL_LOCALE } from './types';
export { useLocaleStore } from './store';

const DICTIONARIES: Record<Locale, Record<string, string>> = { en, uk };

/** Replaces every `{{name}}` in `template` with `String(params[name])`. */
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name) =>
    name in params ? String(params[name]) : match
  );
}

export function useT() {
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);

  const t = (key: string, params?: Record<string, string | number>): string => {
    const dict = DICTIONARIES[locale];
    // Falls back to the English string (never blank, never the raw key) when a key is
    // missing from uk.ts — a missing translation is a bug to fix, not a reason to render
    // nothing. Falls back to the literal key only if it's somehow missing from en.ts too
    // (a real bug in the dictionary itself, not a translation gap).
    const raw = dict[key] ?? en[key] ?? key;
    return interpolate(raw, params);
  };

  return { t, locale, setLocale, intlLocale: INTL_LOCALE[locale] };
}
